import { useMemo } from 'react'
import { encodeQr, qrToSvgPath, type EcLevel } from '@/lib/qr/qrCode'

/**
 * Renders a QR symbol as a single SVG path.
 *
 * Deliberately not theme-aware. A QR is read by a camera, and while most
 * scanners cope with an inverted symbol, plenty still expect dark modules on
 * a light field — so the plate stays light in dark mode too. The surrounding
 * card carries the theming instead.
 */
export default function QrCode({
  value,
  size = 168,
  ecLevel = 'M',
  quietZone = 2,
  label,
  className,
}: {
  value: string
  /** Rendered edge length in CSS pixels. */
  size?: number
  ecLevel?: EcLevel
  /** Light margin in modules. Four is the spec minimum; the card adds padding. */
  quietZone?: number
  /** Accessible name. Never the URL itself — it is not useful read aloud. */
  label: string
  className?: string
}) {
  const symbol = useMemo(() => {
    try {
      const matrix = encodeQr(value, { ecLevel })
      return { path: qrToSvgPath(matrix, quietZone), extent: matrix.size + quietZone * 2 }
    } catch {
      // Only reachable if a payload outgrows version 10, which a link URL
      // cannot. Render nothing rather than tearing down the surrounding card.
      return null
    }
  }, [value, ecLevel, quietZone])

  if (!symbol) return null

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${symbol.extent} ${symbol.extent}`}
      role="img"
      aria-label={label}
      style={{ display: 'block', borderRadius: 6 }}
    >
      <rect width={symbol.extent} height={symbol.extent} fill="#ffffff" />
      <path d={symbol.path} fill="#141414" shapeRendering="crispEdges" />
    </svg>
  )
}
